import { FC, useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import styles from './AIAssistant.module.scss';
import {
  AIAssistantProps,
  ChatMode,
  QuickAction,
  TripPlannerMessage,
  quickActionsTypes,
} from '../../../../../types/aiChat.types';
import {
  useGetChatHistoryQuery,
  useSendChatMessageMutation,
  useSendTripPlannerMessageMutation,
} from '../../../../../store/services/tripsApi';
import QuickActions from './components/QuickActions/QuickActions';
import ChatHistory from './components/ChatHistory/ChatHistory';
import ChatInput from './components/ChatInput/ChatInput';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';

const AIAssistant: FC<AIAssistantProps> = ({ tripId, onClose }) => {
  const isMobile = useIsMobile();
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [tripPlannerMessages, setTripPlannerMessages] = useState<TripPlannerMessage[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [chatMode, setChatMode] = useState<ChatMode>('normal');
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const assistantTitle = chatMode === 'trip_planner' ? 'Trip Planner' : 'AI Assistant';
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const {
    data: chatHistory = [],
    isLoading: isHistoryLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useGetChatHistoryQuery(tripId);

  const [sendMessage, { isLoading: isSending, error: sendError }] = useSendChatMessageMutation();
  const [sendTripPlannerMessage, { isLoading: isSendingPlanner, error: plannerError }] =
    useSendTripPlannerMessageMutation();

  const isSendingAny = chatMode === 'trip_planner' ? isSendingPlanner : isSending;
  const chatError = chatMode === 'trip_planner' ? plannerError : sendError || historyError;

  const quickActions: QuickAction[] = [
    {
      type: quickActionsTypes.trip_planner,
      label: 'Trip Planner',
      message: 'Can you help me plan my trip?',
    },
    {
      type: quickActionsTypes.packing_lists,
      label: 'Packing lists',
      message: 'What should I pack for this trip?',
    },
    {
      type: quickActionsTypes.local_customs,
      label: 'Local customs',
      message: "Can you tell me about the local customs and traditions of the places I'm visiting?",
    },
  ];

  useEffect(() => {
    setTripPlannerMessages([]);
    setCurrentThreadId(undefined);
    setChatMode('normal');
  }, [tripId]);

  useEffect(() => {
    if (chatHistoryRef.current) {
      const scrollElement = chatHistoryRef.current;
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  }, [chatHistory]);

  const handleSendMessage = async (message: string, mode: ChatMode = chatMode, threadIdOverride?: string) => {
    const trimmed = message.trim();
    if (!trimmed) return;

    try {
      if (mode === 'trip_planner') {
        const payload = {
          message: trimmed,
          timezone: userTimezone,
          ...((threadIdOverride ?? currentThreadId) ? { thread_id: threadIdOverride ?? currentThreadId } : {}),
        };

        const result = await sendTripPlannerMessage({
          tripId,
          payload,
        }).unwrap();

        const userMessage: TripPlannerMessage = {
          id: Date.now(),
          text: trimmed,
          sender: 'Human',
          created_at: new Date().toISOString(),
        };

        const aiMessage: TripPlannerMessage = {
          id: Date.now() + 1,
          text: result.message,
          sender: 'ai',
          created_at: new Date().toISOString(),
          thread_id: result.thread_id,
        };

        setTripPlannerMessages(prev => [...prev, userMessage, aiMessage]);

        if (result.thread_id) {
          setCurrentThreadId(result.thread_id);
        }
      } else {
        await sendMessage({
          tripId,
          payload: { user_input: trimmed },
        }).unwrap();
      }

      await refetchHistory();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleQuickAction = async (action: QuickAction) => {
    if (action.type === quickActionsTypes.trip_planner) {
      if (chatMode === 'trip_planner') {
        setChatMode('normal');
        return;
      }

      setChatMode('trip_planner');
      if (tripPlannerMessages.length === 0) {
        await handleSendMessage(action.message, 'trip_planner');
      }
      return;
    }

    setChatMode('normal');
    await handleSendMessage(action.message, 'normal');
  };

  if (isHistoryLoading && !hasInitialLoad) {
    return (
      <Box className={`${styles.aiAssistant} ${isMobile ? styles.mobileAI : ''}`}>
        <Box className={styles.header}>
          <Typography
            variant="h7"
            className={styles.title}
          >
            {assistantTitle}
          </Typography>
          {!isMobile && (
            <IconButton
              onClick={onClose}
              className={styles.closeButton}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Box className={styles.loadingContainer}>
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81' }}
          >
            Loading chat...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!hasInitialLoad && !isHistoryLoading) {
    setHasInitialLoad(true);
  }

  return (
    <Box className={`${styles.aiAssistant} ${isMobile ? styles.mobileAI : ''}`}>
      {!isMobile && (
        <Box className={styles.header}>
          <Box className={styles.headerContent}>
            <Typography
              variant="h5Bold"
              className={styles.title}
            >
              {assistantTitle}
            </Typography>
          </Box>
          <Box className={styles.headerActions}>
            <IconButton
              onClick={onClose}
              className={styles.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Box className={styles.disclaimer}>
          <Typography
            variant="h11"
            className={styles.disclaimerText}
          >
            AI responses may not always be accurate—always verify important details.
          </Typography>
        </Box>
      )}

      <Box className={styles.content}>
        <Box
          ref={chatHistoryRef}
          className={styles.chatHistory}
        >
          <ChatHistory
            messages={chatMode === 'trip_planner' ? tripPlannerMessages : chatHistory}
            isLoading={chatMode === 'trip_planner' ? false : isHistoryLoading}
            error={chatMode === 'trip_planner' ? null : historyError}
            isSending={isSendingAny}
            isTripPlannerMode={chatMode === 'trip_planner'}
          />
        </Box>

        {chatError && (
          <Box className={styles.errorContainer}>
            <Typography
              variant="h11"
              className={styles.errorText}
            >
              {chatMode === 'normal' && historyError
                ? 'Failed to load chat history.'
                : chatError
                  ? 'Failed to send message. Please try again.'
                  : ''}
            </Typography>
          </Box>
        )}

        <Box className={styles.bottomSection}>
          <QuickActions
            actions={quickActions}
            onActionClick={handleQuickAction}
            disabled={isSendingAny}
            tripPlannerActive={chatMode === 'trip_planner'}
          />

          <Box className={styles.inputContainer}>
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isSendingAny}
              placeholder={
                chatMode === 'trip_planner'
                  ? 'Describe or adjust a stop — messages use Trip Planner'
                  : 'Ask any travel questions'
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AIAssistant;
